import { UserType } from "./userType";

// Mirrors the `programs` DB table; source of truth for program indices and
// which user types get which program.
// Program 103 (Equipment Analytics) was retired 2026-08-08: never reachable
// (no user types existed for it, and the chooser requires one), view was a
// stub. The DB still carries its rows until a migration removes them, so the
// portal slice filters it out of fetched program lists.
export const Program = {
  Index: {
    Analytics: 100,
    Training: 101,
    Winsby: 102,
  } as const,

  AllowedUserTypes: {
    100: [UserType.Name.SalesRep, UserType.Name.Manager],
    101: [UserType.Name.LWS_ADMIN, UserType.Name.LWS_DEALER, UserType.Name.LWS_SCHOOL, UserType.Name.LWS_SUPPORT]
  } as const,

  AllowedUserTypesIndex: {
    100: [UserType.Index.SalesRep, UserType.Index.Manager],
    101: [UserType.Index.LWS_ADMIN, UserType.Index.LWS_DEALER, UserType.Index.LWS_SCHOOL, UserType.Index.LWS_SUPPORT]
  } as const,

  DisplayName: {
    100: "Analytics",
    101: "Training",
    102: "Winsby Program",
  } as const,
} as const;

export type ProgramIndex = typeof Program.Index[keyof typeof Program.Index];

/**
 * The program a client user lands in, derived from the type on their account.
 * Most client users never pass through the context chooser, so nothing else
 * sets it; a Manager does pass through (see clientUserPicksContext below), and
 * for them the chooser writes selectedProgram to the store, which wins wherever
 * both are consulted.
 *
 * Lives here rather than beside a view because both the shell and the view need
 * it, and the shell must not import from viewComponents — that dependency runs
 * one way (see CLAUDE.md).
 */
export const programForUserType = (clientTypeName?: string): number | undefined => {
  if (!clientTypeName) return undefined;
  const match = Object.entries(Program.AllowedUserTypes).find(([, types]) =>
    (types as readonly string[]).includes(clientTypeName)
  );
  return match ? Number(match[0]) : undefined;
};

/**
 * Whether a client user is shown the context chooser before landing.
 *
 * Reproduces `frontend_refactor`'s account.tsx: it special-cased Sales_Rep into
 * SaleRepView and the training types into TrainingView, and everything else
 * fell through to the chooser — which in practice meant Manager.
 *
 * Manager needs it because the chooser filters its User Type list by PROGRAM,
 * not by the type on the user's own account, so a Manager can adopt Sales_Rep
 * and review any individual rep's worklist. That is a deliberate capability.
 */
export const clientUserPicksContext = (clientTypeName?: string): boolean =>
  programForUserType(clientTypeName) === Program.Index.Analytics &&
  clientTypeName !== UserType.Name.SalesRep;

/**
 * Which extra scope a user type needs before a context is complete — a sales
 * rep for Sales_Rep, a school/dealer for the training types below LWS_ADMIN /
 * LWS_SUPPORT. One rule for the chooser (which fetches the scope options) and
 * the shell's recent-context restore (which cannot, and must fall back to the
 * chooser instead of opening a dashboard with the scope silently missing).
 */
export const userTypeNeedsSalesRep = (userTypeIndex?: number): boolean =>
  userTypeIndex === UserType.Index.SalesRep;

export const userTypeNeedsSchoolDealer = (userTypeIndex?: number): boolean =>
  userTypeIndex !== undefined &&
  (Program.AllowedUserTypesIndex[Program.Index.Training] as readonly number[]).includes(
    userTypeIndex
  ) &&
  userTypeIndex !== UserType.Index.LWS_ADMIN &&
  userTypeIndex !== UserType.Index.LWS_SUPPORT;
